import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Search,
  Plus,
  Trash2,
  Pencil,
  Package,
  ShieldAlert,
  Car,
  Flame,
  UserX,
  Gavel,
  Home,
  CircleDot,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { fetchSpoaCasesForContext, replaceSpoaCasesForContext } from '../api/spoaCasesApi';
import './RecordingsSpoaModal.css';

export interface EvidencePhoto {
  id: string;
  dataUrl: string;
  slotId: number;
  cameraId?: string;
  cameraName: string;
  wallClockLabel: string;
  videoTimeSec: number;
  /** Actual recording wall-clock timestamp (ms since epoch) */
  wallClockMs?: number;
}

export interface SpoaEvidencePhotoItem {
  photo: EvidencePhoto;
  description: string;
}

export interface SpoaEvidenceEntry {
  id: string;
  description: string;
  recordedAt: string;
  recordedEndAt?: string;
  crimeCategoryId: string;
  items: SpoaEvidencePhotoItem[];
}

export interface RecordingsSpoaDocument {
  id: string;
  spoa: string;
  evidences: SpoaEvidenceEntry[];
  updatedAt: number;
}

export const SPOA_CRIME_CATEGORIES: { id: string; code: string; name: string; Icon: LucideIcon }[] = [
  { id: 'cat-hurto', code: 'HURTO', name: 'Hurto / Robo', Icon: Package },
  { id: 'cat-violencia', code: 'VIOLENCIA', name: 'Lesiones / Violencia', Icon: ShieldAlert },
  { id: 'cat-transito', code: 'TRANSITO', name: 'Tránsito / Vehículo', Icon: Car },
  { id: 'cat-danos', code: 'DANOS', name: 'Daños / Vandalismo', Icon: Flame },
  { id: 'cat-personas', code: 'PERSONAS', name: 'Personas / Desaparición', Icon: UserX },
  { id: 'cat-sustancias', code: 'SUSTANCIAS', name: 'Sustancias', Icon: CircleDot },
  { id: 'cat-inmueble', code: 'INMUEBLE', name: 'Inmueble / Allanamiento', Icon: Home },
  { id: 'cat-judicial', code: 'JUDICIAL', name: 'Orden judicial', Icon: Gavel },
  { id: 'cat-otros', code: 'OTROS', name: 'Otros', Icon: HelpCircle },
];

const crimeById = new Map(SPOA_CRIME_CATEGORIES.map((c) => [c.id, c]));

function newId(prefix: string): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `${prefix}-${crypto.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function clonePhoto(p: EvidencePhoto): EvidencePhoto {
  return { ...p };
}

function cloneItem(it: SpoaEvidencePhotoItem): SpoaEvidencePhotoItem {
  return { photo: clonePhoto(it.photo), description: it.description };
}

function cloneEvidence(e: SpoaEvidenceEntry): SpoaEvidenceEntry {
  return {
    id: e.id,
    description: e.description,
    recordedAt: e.recordedAt,
    crimeCategoryId: e.crimeCategoryId,
    items: e.items.map(cloneItem),
  };
}

function isLikelyImageDataUrl(s: string | undefined | null): boolean {
  if (s == null || typeof s !== 'string') return false;
  const t = s.trim();
  return t.startsWith('data:image/') && t.length > 80;
}

export interface SpoaCameraInfo {
  id: string;
  name: string;
  siteAddress?: string;
  siteName?: string;
  status?: string;
  channel?: string;
  ip?: string;
  latitude?: string;
  longitude?: string;
}

export interface RecordingsSpoaModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
  contextKey: string;
  evidencePhotos: EvidencePhoto[];
  activeCameras?: SpoaCameraInfo[];
}

interface CameraEvidenceData {
  description: string;
  crimeId: string;
  photoCaptions: Record<string, string>;
}

type ModalView = 'consult' | 'create';

const emptyCamData = (): CameraEvidenceData => ({ description: '', crimeId: '', photoCaptions: {} });

const RecordingsSpoaModal: React.FC<RecordingsSpoaModalProps> = ({
  open,
  onClose,
  onSaved,
  contextKey,
  evidencePhotos,
  activeCameras = [],
}) => {
  const { token } = useAuth();

  const [savedCases, setSavedCases] = useState<RecordingsSpoaDocument[]>([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [view, setView] = useState<ModalView>('consult');

  const [searchQuery, setSearchQuery] = useState('');
  const [previewCaseId, setPreviewCaseId] = useState<string | null>(null);

  const [editingCaseId, setEditingCaseId] = useState<string | null>(null);
  const [isNewCase, setIsNewCase] = useState(false);
  const [spoa, setSpoa] = useState('');
  const [caseEvidences, setCaseEvidences] = useState<SpoaEvidenceEntry[]>([]);
  const [evidenceName, setEvidenceName] = useState('');
  const [evRecordedAt, setEvRecordedAt] = useState('');
  const [selectedCameraName, setSelectedCameraName] = useState<string | null>(null);
  const [perCameraData, setPerCameraData] = useState<Record<string, CameraEvidenceData>>({});
  const [fullscreen, setFullscreen] = useState<EvidencePhoto | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  /** Guardar + pestaña Certificado de descargas */
  const [saveExportBusy, setSaveExportBusy] = useState(false);

  const evidenceRef = useRef(evidencePhotos);
  evidenceRef.current = evidencePhotos;

  const cameraGroups = useMemo(() => {
    const map = new Map<string, { name: string; count: number; cameraId?: string }>();
    evidencePhotos.forEach((p) => {
      const key = p.cameraName;
      const existing = map.get(key);
      if (existing) existing.count++;
      else map.set(key, { name: p.cameraName, count: 1, cameraId: p.cameraId });
    });
    return Array.from(map.values());
  }, [evidencePhotos]);

  useEffect(() => {
    if (!open) {
      setCasesLoading(false);
      return;
    }
    setView('consult');
    setSearchQuery('');
    setPreviewCaseId(null);
    setEditingCaseId(null);
    setIsNewCase(false);
    setSpoa('');
    setCaseEvidences([]);
    setEvidenceName('');
    setEvRecordedAt('');
    setSelectedCameraName(null);
    setPerCameraData({});
    setFullscreen(null);
    setFormError(null);

    if (!token) {
      setSavedCases([]);
      setCasesLoading(false);
      return;
    }

    let cancelled = false;
    setCasesLoading(true);
    fetchSpoaCasesForContext(contextKey, token)
      .then((docs) => {
        if (!cancelled) setSavedCases(docs);
      })
      .catch(() => {
        if (!cancelled) {
          setSavedCases([]);
          toast.error('No se pudieron cargar los casos SPOA.');
        }
      })
      .finally(() => {
        if (!cancelled) setCasesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, contextKey, token]);

  useEffect(() => {
    if (!open) setFullscreen(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (fullscreen) setFullscreen(null);
      else if (view === 'create' && isNewCase) setView('consult');
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, fullscreen, view, isNewCase]);

  const sortedCases = useMemo(
    () => [...savedCases].sort((a, b) => b.updatedAt - a.updatedAt),
    [savedCases],
  );

  const filteredCases = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedCases;
    return sortedCases.filter(
      (c) =>
        (c.spoa || '').toLowerCase().includes(q) ||
        c.evidences.some((ev) => (ev.description || '').toLowerCase().includes(q)),
    );
  }, [sortedCases, searchQuery]);

  /** Al cargar resultados o cambiar el filtro, mantener una selección válida y mostrar pruebas en el panel derecho. */
  useEffect(() => {
    if (!open || casesLoading) return;
    setPreviewCaseId((prev) => {
      if (filteredCases.length === 0) return null;
      if (prev != null && filteredCases.some((c) => c.id === prev)) return prev;
      return filteredCases[0]!.id;
    });
  }, [open, casesLoading, filteredCases]);

  const previewCase = useMemo(() => {
    if (!previewCaseId) return null;
    return savedCases.find((c) => c.id === previewCaseId) ?? null;
  }, [savedCases, previewCaseId]);

  const displayPhotos = useMemo(() => {
    if (!selectedCameraName) return [];
    return evidencePhotos.filter((p) => p.cameraName === selectedCameraName);
  }, [evidencePhotos, selectedCameraName]);

  const currentCamData = useMemo((): CameraEvidenceData => {
    if (!selectedCameraName) return emptyCamData();
    return perCameraData[selectedCameraName] ?? emptyCamData();
  }, [selectedCameraName, perCameraData]);

  const siteInfo = useMemo(() => {
    if (activeCameras.length === 0) return null;
    const cam = activeCameras[0];
    return {
      address: cam.siteAddress || '',
      siteName: cam.siteName || '',
      status: cam.status || '',
      channel: cam.channel || '',
      ip: cam.ip || '',
      latitude: cam.latitude || '',
      longitude: cam.longitude || '',
    };
  }, [activeCameras]);

  const generateEvidenceName = useCallback((spoaVal: string) => {
    const now = new Date();
    const ts = now.toISOString().replace(/[-:T.Z]/g, '').slice(0, 14);
    return `${spoaVal || 'ev'}${ts}`;
  }, []);

  const updateCamField = useCallback((camName: string, field: 'description' | 'crimeId', value: string) => {
    setPerCameraData((prev) => {
      const existing = prev[camName] ?? emptyCamData();
      return { ...prev, [camName]: { ...existing, [field]: value } };
    });
  }, []);

  const updatePhotoCaption = useCallback((camName: string, photoId: string, caption: string) => {
    setPerCameraData((prev) => {
      const existing = prev[camName] ?? emptyCamData();
      return {
        ...prev,
        [camName]: {
          ...existing,
          photoCaptions: { ...existing.photoCaptions, [photoId]: caption },
        },
      };
    });
  }, []);

  const enterCreateView = useCallback(
    (opts: { caseId: string | null; isNew: boolean; spoaVal: string; existingEvidences: SpoaEvidenceEntry[] }) => {
      setEditingCaseId(opts.caseId);
      setIsNewCase(opts.isNew);
      setSpoa(opts.spoaVal);
      setCaseEvidences(opts.existingEvidences);
      setEvidenceName(generateEvidenceName(opts.spoaVal));
      setEvRecordedAt(new Date().toISOString());
      setPerCameraData({});
      setFormError(null);
      setFullscreen(null);
      const firstCam = cameraGroups.length > 0 ? cameraGroups[0].name : null;
      setSelectedCameraName(firstCam);
      setView('create');
    },
    [generateEvidenceName, cameraGroups],
  );

  const handleOpenExistingCase = useCallback(
    (doc: RecordingsSpoaDocument) => {
      const legacyCod = (doc as { codPolicia?: string }).codPolicia;
      const spoaVal = (doc.spoa ?? '').trim() || (legacyCod ?? '').trim();
      enterCreateView({
        caseId: doc.id,
        isNew: false,
        spoaVal,
        existingEvidences: doc.evidences.map(cloneEvidence),
      });
    },
    [enterCreateView],
  );

  const handleNewCase = useCallback(() => {
    enterCreateView({ caseId: null, isNew: true, spoaVal: '', existingEvidences: [] });
  }, [enterCreateView]);

  const handleSaveEvidence = async () => {
    const s = spoa.trim();
    if (!s) {
      setFormError('Ingresa el número SPOA / expediente.');
      return;
    }

    const newEvidences: SpoaEvidenceEntry[] = [];

    for (const cam of cameraGroups) {
      const camPhotos = evidencePhotos.filter((p) => p.cameraName === cam.name);
      if (camPhotos.length === 0) continue;

      const camData = perCameraData[cam.name] ?? emptyCamData();

      const items: SpoaEvidencePhotoItem[] = camPhotos.map((p) => ({
        photo: clonePhoto(p),
        description: (camData.photoCaptions[p.id] ?? '').trim(),
      }));

      const wallClocks = camPhotos
        .map((p) => p.wallClockMs)
        .filter((ms): ms is number => typeof ms === 'number' && ms > 0);

      let startIso = evRecordedAt;
      let endIso: string | undefined;
      if (wallClocks.length > 0) {
        const minMs = Math.min(...wallClocks);
        const maxMs = Math.max(...wallClocks);
        startIso = new Date(minMs).toISOString();
        endIso = new Date(maxMs + 5 * 60_000).toISOString();
      }

      newEvidences.push({
        id: newId('ev'),
        description: camData.description.trim(),
        recordedAt: startIso,
        recordedEndAt: endIso,
        crimeCategoryId: camData.crimeId || SPOA_CRIME_CATEGORIES[0].id,
        items,
      });
    }

    if (newEvidences.length === 0) {
      setFormError('No hay capturas para generar evidencias.');
      return;
    }

    const evidencesForDoc = [...caseEvidences.map(cloneEvidence), ...newEvidences];
    setFormError(null);

    const now = Date.now();
    const id = editingCaseId ?? newId('case');
    const doc: RecordingsSpoaDocument = {
      id,
      spoa: s,
      evidences: evidencesForDoc,
      updatedAt: now,
    };

    if (!token) {
      toast.error('Inicie sesión para guardar en la base de datos.');
      return;
    }

    const next = [...savedCases];
    const i = next.findIndex((d) => d.id === id);
    if (i >= 0) next[i] = doc;
    else next.push(doc);

    setSaveExportBusy(true);
    try {
      await replaceSpoaCasesForContext(contextKey, next, token);
      setSavedCases(next);
      setFullscreen(null);
      onSaved?.();
      toast.success('Evidencia guardada correctamente.');
      onClose();
    } catch (err: unknown) {
      let msg = 'No se pudo guardar el caso en la base de datos.';
      if (err && typeof err === 'object' && 'response' in err) {
        const resp = (err as { response?: { data?: { error?: { message?: string } | string; message?: string }; status?: number } }).response;
        const d = resp?.data;
        const detail =
          (typeof d?.error === 'object' && d.error && 'message' in d.error ? (d.error as { message: string }).message : '') ||
          (typeof d?.error === 'string' ? d.error : '') ||
          (typeof d?.message === 'string' ? d.message : '');
        if (detail) msg = detail;
        else if (resp?.status === 413) msg = 'El tamaño de las imágenes excede el límite permitido.';
      }
      toast.error(msg, { duration: 6000 });
    } finally {
      setSaveExportBusy(false);
    }
  };

  const handleDeleteCase = useCallback(
    async (docId: string) => {
      if (!token) return;
      const next = savedCases.filter((d) => d.id !== docId);
      try {
        await replaceSpoaCasesForContext(contextKey, next, token);
        setSavedCases(next);
        if (previewCaseId === docId) setPreviewCaseId(null);
      } catch {
        toast.error('No se pudo eliminar el caso.');
      }
    },
    [token, savedCases, contextKey, previewCaseId],
  );

  if (!open) return null;

  /* ================================================================
     Vista 1 — Consultar SPOA / COD.POLICIA
     ================================================================ */
  const consultView = (
    <>
      <div className="rspoa-header">
        <h2 className="rspoa-header__title">Consultar SPOA / COD.POLICIA</h2>
        <button type="button" className="rspoa-header__close" onClick={onClose} aria-label="Cerrar">
          <X size={18} />
        </button>
      </div>

      <div className="rspoa-search">
        <input
          type="text"
          className="rspoa-search__input"
          placeholder="Buscar"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoComplete="off"
        />
        <Search size={18} className="rspoa-search__icon" />
      </div>

      <div className="rspoa-body rspoa-body--consult">
        <div className="rspoa-results">
          <h3 className="rspoa-panel-title">Resultados</h3>
          <div className="rspoa-results__scroll">
            {casesLoading ? (
              <p className="rspoa-muted-text">Cargando casos…</p>
            ) : filteredCases.length === 0 ? (
              <p className="rspoa-muted-text">
                {searchQuery.trim() ? 'Sin resultados.' : 'No hay casos registrados.'}
              </p>
            ) : (
              filteredCases.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  className={`rspoa-results__item ${previewCaseId === doc.id ? 'rspoa-results__item--active' : ''}`}
                  onClick={() => setPreviewCaseId(doc.id)}
                  onDoubleClick={() => handleOpenExistingCase(doc)}
                >
                  <span>{doc.spoa || '(sin SPOA)'}</span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="rspoa-pruebas">
          <h3 className="rspoa-panel-title">Pruebas asociadas</h3>
          {previewCase ? (
            <div className="rspoa-pruebas__content">
              {previewCase.evidences.length === 0 ? (
                <p className="rspoa-muted-text">No hay evidencias en este caso.</p>
              ) : (
                <ul className="rspoa-pruebas__list">
                  {previewCase.evidences.map((ev) => {
                    const cat = crimeById.get(ev.crimeCategoryId);
                    let dateStr = '';
                    try {
                      dateStr = new Date(ev.recordedAt).toLocaleString('es-ES', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      });
                    } catch {
                      dateStr = ev.recordedAt;
                    }
                    return (
                      <li key={ev.id} className="rspoa-prueba-card">
                        <div className="rspoa-prueba-card__info">
                          <strong>{cat?.name || 'Sin categoría'}</strong>
                          <span className="rspoa-prueba-card__date">{dateStr}</span>
                          <p className="rspoa-prueba-card__desc">
                            {ev.description || <em>Sin descripción</em>}
                          </p>
                        </div>
                        <div className="rspoa-prueba-card__thumbs">
                          {ev.items.slice(0, 4).map((it) => (
                            <div key={it.photo.id} className="rspoa-prueba-card__thumb">
                              {isLikelyImageDataUrl(it.photo.dataUrl) ? (
                                <img src={it.photo.dataUrl} alt="" />
                              ) : (
                                <span className="rspoa-thumb-placeholder">?</span>
                              )}
                            </div>
                          ))}
                          {ev.items.length > 4 && (
                            <span className="rspoa-prueba-card__more">+{ev.items.length - 4}</span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="rspoa-pruebas__actions">
                <button
                  type="button"
                  className="rspoa-btn rspoa-btn--sm rspoa-btn--primary"
                  onClick={() => handleOpenExistingCase(previewCase)}
                >
                  <Pencil size={14} /> Editar / Añadir evidencia
                </button>
                <button
                  type="button"
                  className="rspoa-btn rspoa-btn--sm rspoa-btn--danger"
                  onClick={() => handleDeleteCase(previewCase.id)}
                >
                  <Trash2 size={14} /> Eliminar caso
                </button>
              </div>
            </div>
          ) : (
            <p className="rspoa-placeholder-text">Seleccione un SPOA / COD.POLICIA para visualizar.</p>
          )}
        </div>
      </div>

      <div className="rspoa-footer">
        {!previewCase ? (
          <button type="button" className="rspoa-btn rspoa-btn--outline" onClick={handleNewCase}>
            <Plus size={16} /> Nuevo caso SPOA
          </button>
        ) : (
          <div className="rspoa-footer__spacer" />
        )}
        <div className="rspoa-footer__spacer" />
        <button type="button" className="rspoa-btn rspoa-btn--ghost" onClick={onClose}>
          Cancelar
        </button>
      </div>
    </>
  );

  /* ================================================================
     Vista 2 — Crear evidencia (1 evidencia por cámara)
     ================================================================ */
  const hasSiteInfo = siteInfo && (siteInfo.address || siteInfo.siteName || siteInfo.channel || siteInfo.ip);

  const createView = (
    <>
      <div className="rspoa-header">
        <h2 className="rspoa-header__title">Crear evidencia.</h2>
        <button type="button" className="rspoa-header__close" onClick={onClose} aria-label="Cerrar">
          <X size={18} />
        </button>
      </div>

      <div className="rspoa-body rspoa-body--create">
        {/* --- Sidebar: SPOA + nombre + cámaras como evidencias seleccionables --- */}
        <div className="rspoa-sidebar">
          {!isNewCase ? (
            <div className="rspoa-sidebar__spoa-label">
              <strong>SPOA / COD.POLICIA:</strong> {spoa || '—'}
            </div>
          ) : (
            <div className="rspoa-sidebar__field">
              <label>SPOA / COD.POLICIA:</label>
              <input
                type="text"
                value={spoa}
                onChange={(e) => {
                  setSpoa(e.target.value);
                  setEvidenceName(generateEvidenceName(e.target.value));
                }}
                placeholder="Ej. 2025001"
                autoComplete="off"
              />
            </div>
          )}

          <div className="rspoa-sidebar__field">
            <label>Evidencia:</label>
            <input
              type="text"
              value={evidenceName}
              onChange={(e) => setEvidenceName(e.target.value)}
              placeholder="Nombre de evidencia"
              autoComplete="off"
            />
          </div>

          <div className="rspoa-sidebar__cameras">
            {cameraGroups.map((cam) => (
              <button
                key={cam.name}
                type="button"
                className={`rspoa-camera-item ${selectedCameraName === cam.name ? 'rspoa-camera-item--active' : ''}`}
                onClick={() => setSelectedCameraName(cam.name)}
              >
                <span className="rspoa-camera-item__name">{cam.name}</span>
                <span className="rspoa-camera-badge">{cam.count}</span>
              </button>
            ))}
            {cameraGroups.length === 0 && (
              <p className="rspoa-muted-text rspoa-muted-text--small">Sin capturas</p>
            )}
          </div>
        </div>

        {/* --- Panel derecho: formulario para la cámara seleccionada --- */}
        <div className="rspoa-content">
          {!selectedCameraName ? (
            <p className="rspoa-placeholder-text">Seleccione una cámara de la lista para editar su evidencia.</p>
          ) : (
            <>
              <h3 className="rspoa-section-title">
                Informacion de la evidencia — {selectedCameraName}
              </h3>

              {formError && <p className="rspoa-error">{formError}</p>}

              {hasSiteInfo && (
                <div className="rspoa-site-box">
                  <h4 className="rspoa-site-box__title">Datos del sitio</h4>
                  {siteInfo!.address && (
                    <p><strong>Direccion:</strong> {siteInfo!.address}</p>
                  )}
                  {(siteInfo!.latitude || siteInfo!.longitude) && (
                    <p>
                      <strong>Latitud:</strong> {siteInfo!.latitude || '—'}{' '}
                      <strong>longitud:</strong> {siteInfo!.longitude || '—'}
                    </p>
                  )}
                  {siteInfo!.channel && (
                    <p><strong>Canal:</strong> {siteInfo!.channel}</p>
                  )}
                  {siteInfo!.ip && (
                    <p><strong>IP XTAM:</strong> {siteInfo!.ip}</p>
                  )}
                  {siteInfo!.status && (
                    <p><strong>Estado:</strong> {siteInfo!.status}</p>
                  )}
                </div>
              )}

              <div className="rspoa-form-row">
                <div className="rspoa-field rspoa-field--grow">
                  <label>Resumen de los hechos:</label>
                  <textarea
                    value={currentCamData.description}
                    onChange={(e) => updateCamField(selectedCameraName, 'description', e.target.value)}
                    rows={4}
                    placeholder="Describa los hechos observados para esta cámara"
                  />
                </div>

                <div className="rspoa-field">
                  <label>Delitos</label>
                  <select
                    value={currentCamData.crimeId}
                    onChange={(e) => updateCamField(selectedCameraName, 'crimeId', e.target.value)}
                  >
                    <option value="">Selecciona...</option>
                    {SPOA_CRIME_CATEGORIES.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <h3 className="rspoa-section-title">Capturas de pantalla</h3>

              {displayPhotos.length === 0 ? (
                <p className="rspoa-muted-text">No hay capturas para esta cámara.</p>
              ) : (
                <div className="rspoa-captures-detail">
                  {displayPhotos.map((p, i) => (
                    <div key={p.id} className="rspoa-capture-row">
                      <div className="rspoa-capture-row__preview">
                        <span className="rspoa-capture-row__num">{i + 1}</span>
                        <button
                          type="button"
                          className="rspoa-capture-row__thumb"
                          onClick={() => setFullscreen(p)}
                          title={`${p.cameraName} — ${p.wallClockLabel}`}
                        >
                          {isLikelyImageDataUrl(p.dataUrl) ? (
                            <img src={p.dataUrl} alt="" decoding="async" />
                          ) : (
                            <span className="rspoa-capture__fallback">Captura de Pantalla</span>
                          )}
                        </button>
                        <span className="rspoa-capture-row__time">{p.wallClockLabel}</span>
                      </div>
                      <div className="rspoa-capture-row__desc">
                        <label htmlFor={`cap-desc-${p.id}`}>Descripción:</label>
                        <textarea
                          id={`cap-desc-${p.id}`}
                          value={currentCamData.photoCaptions[p.id] ?? ''}
                          onChange={(e) => updatePhotoCaption(selectedCameraName, p.id, e.target.value)}
                          rows={2}
                          placeholder="Descripción de esta captura"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="rspoa-footer rspoa-footer--right">
        <button
          type="button"
          className="rspoa-btn rspoa-btn--ghost"
          onClick={() => {
            if (isNewCase) setView('consult');
            else onClose();
          }}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="rspoa-btn rspoa-btn--dark"
          onClick={() => void handleSaveEvidence()}
          disabled={saveExportBusy}
        >
          {saveExportBusy ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </>
  );

  /* ================================================================
     Render
     ================================================================ */
  const modal = (
    <div
      className="rspoa-overlay"
      role="presentation"
    >
      <div
        className="rspoa-modal"
        role="dialog"
        aria-labelledby="rspoa-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {view === 'consult' ? consultView : createView}
      </div>
    </div>
  );

  const fs =
    fullscreen &&
    createPortal(
      <div className="rspoa-fullscreen" role="presentation" onClick={() => setFullscreen(null)}>
        <button
          type="button"
          className="rspoa-fullscreen__close"
          onClick={() => setFullscreen(null)}
          aria-label="Cerrar vista completa"
        >
          ×
        </button>
        {isLikelyImageDataUrl(fullscreen.dataUrl) ? (
          <img
            src={fullscreen.dataUrl}
            alt="Evidencia ampliada"
            onClick={(e) => e.stopPropagation()}
            decoding="async"
          />
        ) : (
          <p className="rspoa-fullscreen__missing">No hay imagen para mostrar.</p>
        )}
      </div>,
      document.body,
    );

  return (
    <>
      {createPortal(modal, document.body)}
      {fs}
    </>
  );
};

export default RecordingsSpoaModal;
